import { assertPageContextProvidedByUser } from '../assertPageContextProvidedByUser.js';
import { assertUsage, hasProp, isObjectWithKeys, objectAssign, assertWarning, assertUsageUrl, joinEnglish } from './utils.js';
import { assertRouteParams, assertSyncRouting } from './resolveRouteFunction.js';
import pc from '@brillout/picocolors';
export { executeOnBeforeRouteHook };
async function executeOnBeforeRouteHook(onBeforeRouteHook, pageContext) {
    let hookReturn = onBeforeRouteHook.onBeforeRoute(pageContext);
    assertSyncRouting(hookReturn, `The onBeforeRoute() hook ${onBeforeRouteHook.hookFilePath}`);
    hookReturn = await hookReturn;
    const errPrefix = `The onBeforeRoute() hook defined by ${onBeforeRouteHook.hookFilePath}`;
    assertUsage(hookReturn === null ||
        hookReturn === undefined ||
        (isObjectWithKeys(hookReturn, ['pageContext']) && hasProp(hookReturn, 'pageContext')), `${errPrefix} should return ${pc.cyan('null')}, ${pc.cyan('undefined')}, or a plain JavaScript object ${pc.cyan('{ pageContext: { /* ... */ } }')}`);
    if (hookReturn === null || hookReturn === undefined) {
        return null;
    }
    assertUsage(hasProp(hookReturn, 'pageContext', 'object'), `${errPrefix} returned ${pc.cyan('{ pageContext }')} but pageContext should be a plain JavaScript object.`);
    if (hasProp(hookReturn.pageContext, '_pageId') && !hasProp(hookReturn.pageContext, '_pageId', 'null')) {
        const errPrefix2 = `${errPrefix} returned ${pc.cyan('{ pageContext: { _pageId } }')} but ${pc.cyan('_pageId')} should be`;
        assertUsage(hasProp(hookReturn.pageContext, '_pageId', 'string'), `${errPrefix2} a string or null`);
        assertUsage(pageContext._allPageIds.includes(hookReturn.pageContext._pageId), `${errPrefix2} ${joinEnglish(pageContext._allPageIds.map((s) => pc.cyan(s)), 'or')}`);
    }
    if (hasProp(hookReturn.pageContext, 'routeParams')) {
        assertRouteParams(hookReturn.pageContext, `${errPrefix} returned ${pc.cyan('{ pageContext: { routeParams } }')} but routeParams should`);
    }
    const pageContextAddendumHook = {};
    if (hasProp(hookReturn.pageContext, 'url')) {
        assertWarning(false, `${errPrefix} returned ${pc.cyan('{ pageContext: { url } }')} but ${pc.cyan('pageContext.url')} has been renamed to ${pc.cyan('pageContext.urlOriginal')}. Return ${pc.cyan('{ pageContext: { urlOriginal } }')} instead. (See https://vite-plugin-ssr.com/migration/0.4.23 for more information.)`, { onlyOnce: true });
        hookReturn.pageContext.urlOriginal = hookReturn.pageContext.url;
        delete hookReturn.pageContext.url;
    }
    if (hasProp(hookReturn.pageContext, 'urlOriginal')) {
        assertUsageUrl(hookReturn.pageContext.urlOriginal, `${errPrefix} returned ${pc.cyan('{ pageContext: { urlOriginal } }')} but ${pc.cyan('urlOriginal')}`);
        // Ugly workaround: ideally urlOriginal should be immutable.
        //  - Instead of using pageContext._urlOriginalPristine, maybe we can keep pageContext.urlOriginal immutable while re-using `pageContext._urlRewrite`.
        //  - Or better yet we rename pageContext._urlRewrite to pageContext.urlLogical and we allow the user to override pageContext.urlLogical, and we rename pageContext.urlOriginal => `pageContext.urlReal`.
        //    - pageContext.urlReal / pageContext.urlLogical
        //                         VS
        //      pageContext.urlReal / pageContext.urlModified
        //                         VS
        //      pageContext.urlOriginal / pageContext.urlModified
        objectAssign(pageContextAddendumHook, { _urlOriginalPristine: pageContext.urlOriginal });
    }
    assertPageContextProvidedByUser(hookReturn.pageContext, {
        hookFilePath: onBeforeRouteHook.hookFilePath,
        hookName: 'onBeforeRoute'
    });
    objectAssign(pageContextAddendumHook, hookReturn.pageContext);
    return pageContextAddendumHook;
}
